import { queryOptions, useQuery } from '@tanstack/react-query';

import { apiClient } from '@cast/infrastructure-api';
import { useCurrentOrganizationId } from '@cast/infrastructure-organization';
import { makeIsEnabled } from '@cast/utils';

const PERSISTENT_VOLUMES_AUTOSCALING_CONFIG_QUERY_KEY =
  'persistent-volumes-autoscaling-config';

type Params = {
  organizationId: string;
  clusterId?: string;
  enabled?: boolean;
};

const getPersistentVolumesAutoscalingConfigQueryKey = ({
  organizationId,
  clusterId,
}: Partial<Omit<Params, 'enabled'>> = {}) =>
  [
    PERSISTENT_VOLUMES_AUTOSCALING_CONFIG_QUERY_KEY,
    organizationId,
    clusterId,
  ] as const;

const getPersistentVolumesAutoscalingConfigQueryOptions = ({
  organizationId,
  clusterId,
  enabled = true,
}: Params) =>
  queryOptions({
    queryKey: getPersistentVolumesAutoscalingConfigQueryKey({
      organizationId,
      clusterId,
    }),
    queryFn: async () => {
      const { data } =
        await apiClient.stop.getPersistentVolumesAutoscalingConfig({
          organizationId,
          clusterId: clusterId!,
        });
      return data;
    },
    enabled: makeIsEnabled(!!organizationId, !!clusterId, enabled),
  });

type UsePersistentVolumesAutoscalingConfigQueryArgs = Omit<
  Params,
  'organizationId'
>;

export const usePersistentVolumesAutoscalingConfigQuery = (
  args: UsePersistentVolumesAutoscalingConfigQueryArgs = {}
) => {
  const organizationId = useCurrentOrganizationId();

  const query = useQuery(
    getPersistentVolumesAutoscalingConfigQueryOptions({
      ...args,
      organizationId,
    })
  );

  return {
    ...query,
    data: query.data || {},
  };
};
